'use client';

import React from 'react';
import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';

/**
 * État du service API
 */
export interface ApiServiceState {
  available: boolean;
  lastChecked: number;
  errors: ApiErrorRecord[];
  endpoints: {
    [key: string]: {
      available: boolean;
      lastStatus: number;
      lastChecked: number;
    };
  };
}

/**
 * Structure d'un enregistrement d'erreur API
 */
export interface ApiErrorRecord {
  timestamp: number;
  endpoint: string;
  status?: number;
  message: string;
  code?: string;
}

/**
 * Classe pour gérer les erreurs d'API et l'état du service
 */
export class ApiErrorHandler {
  private static instance: ApiErrorHandler;
  private state: ApiServiceState;
  private maxErrorsHistory: number = 50;
  private listeners: ((state: ApiServiceState) => void)[] = [];

  private constructor() {
    this.state = {
      available: true,
      lastChecked: Date.now(),
      errors: [],
      endpoints: {}
    };

    // Récupérer l'état du localStorage s'il existe
    if (typeof window !== 'undefined') {
      try {
        const savedState = localStorage.getItem('sentinel_api_state');
        if (savedState) {
          const parsedState = JSON.parse(savedState);
          // Ne garder que les erreurs récentes (moins de 24h)
          const recentTime = Date.now() - 86400000; // 24h en millisecondes
          parsedState.errors = parsedState.errors?.filter((err: ApiErrorRecord) => err.timestamp > recentTime) || [];
          this.state = {
            ...this.state,
            ...parsedState
          };
        }
      } catch (e) {
        console.warn('Erreur lors de la récupération de l\'état API du localStorage', e);
      }
    }
  }

  /**
   * Obtenir l'instance unique du gestionnaire d'erreurs
   */
  public static getInstance(): ApiErrorHandler {
    if (!ApiErrorHandler.instance) {
      ApiErrorHandler.instance = new ApiErrorHandler();
    }
    return ApiErrorHandler.instance;
  }

  /**
   * Enregistrer un écouteur pour les changements d'état
   */
  public subscribe(listener: (state: ApiServiceState) => void): () => void {
    this.listeners.push(listener);
    // Appeler le listener immédiatement avec l'état actuel
    listener(this.state);
    
    // Retourner une fonction pour se désabonner
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }

  /**
   * Mettre à jour les écouteurs
   */
  private notifyListeners(): void {
    this.listeners.forEach(listener => listener(this.state));
  }

  /**
   * Sauvegarder l'état dans le localStorage
   */
  private saveState(): void {
    if (typeof window !== 'undefined') {
      try {
        localStorage.setItem('sentinel_api_state', JSON.stringify(this.state));
      } catch (e) {
        console.warn('Erreur lors de la sauvegarde de l\'état API dans le localStorage', e);
      }
    }
  }

  /**
   * Mettre à jour l'état global de disponibilité de l'API
   */
  public updateApiAvailability(available: boolean): void {
    this.state.available = available;
    this.state.lastChecked = Date.now();
    this.saveState();
    this.notifyListeners();
  }

  /**
   * Enregistrer une erreur d'API
   * @param error L'erreur Axios à enregistrer
   * @param context Contexte supplémentaire pour l'erreur (optionnel)
   */
  public recordApiError(error: AxiosError<unknown>, context?: string): void {
    const endpoint = error.config?.url || 'unknown';
    const status = error.response?.status;
    const message = context ? `${context}: ${error.message || 'Erreur inconnue'}` : (error.message || 'Erreur inconnue');
    const code = error.code;

    // Ajouter l'erreur à l'historique
    this.state.errors.unshift({
      timestamp: Date.now(),
      endpoint,
      status,
      message,
      code
    });

    // Limiter la taille de l'historique
    if (this.state.errors.length > this.maxErrorsHistory) {
      this.state.errors = this.state.errors.slice(0, this.maxErrorsHistory);
    }

    // Mettre à jour l'état de l'endpoint
    if (endpoint !== 'unknown') {
      this.state.endpoints[endpoint] = {
        available: false,
        lastStatus: status || 0,
        lastChecked: Date.now()
      };
    }

    // Si erreur de connexion réseau, marquer le service comme indisponible
    if (error.code === 'ERR_NETWORK' || !error.response) {
      this.updateApiAvailability(false);
    }

    this.saveState();
    this.notifyListeners();
  }

  /**
   * Enregistrer un succès d'API
   */
  public recordApiSuccess(endpoint: string, status: number = 200): void {
    // Mettre à jour l'état de l'endpoint
    this.state.endpoints[endpoint] = {
      available: true,
      lastStatus: status,
      lastChecked: Date.now()
    };

    // Si précédemment indisponible, marquer comme disponible à nouveau
    if (!this.state.available) {
      this.updateApiAvailability(true);
    }

    this.saveState();
    this.notifyListeners();
  }

  /**
   * Obtenir l'état actuel
   */
  public getState(): ApiServiceState {
    return {...this.state};
  }

  /**
   * Vérifier si un endpoint spécifique est disponible
   */
  public isEndpointAvailable(endpoint: string): boolean {
    return this.state.endpoints[endpoint]?.available !== false;
  }

  /**
   * Obtenir l'historique des erreurs
   */
  public getErrorHistory(): ApiErrorRecord[] {
    return [...this.state.errors];
  }

  /**
   * Effacer l'historique des erreurs
   */
  public clearErrorHistory(): void {
    this.state.errors = [];
    this.saveState();
    this.notifyListeners();
  }

  /**
   * Vérifier si l'API est disponible en général
   */
  public isApiAvailable(): boolean {
    return this.state.available;
  }

  /**
   * Gérer une erreur générique
   * @param error L'erreur à gérer
   * @param context Contexte supplémentaire pour l'erreur (optionnel)
   */
  public handleError(error: Error, context?: string): void {
    const errorRecord: ApiErrorRecord = {
      timestamp: Date.now(),
      endpoint: context || 'unknown',
      message: error.message,
      code: 'GENERIC_ERROR'
    };

    // Ajouter l'erreur à l'historique
    this.state.errors.unshift(errorRecord);

    // Limiter la taille de l'historique
    if (this.state.errors.length > this.maxErrorsHistory) {
      this.state.errors = this.state.errors.slice(0, this.maxErrorsHistory);
    }

    this.saveState();
    this.notifyListeners();
  }

  /**
   * Obtenir un message d'erreur convivial basé sur l'erreur Axios
   */
  public static getErrorMessage(error: AxiosError<unknown>): string {
    // Si pas de réponse (erreur réseau)
    if (!error.response) {
      return 'Impossible de se connecter au serveur. Vérifiez votre connexion internet.';
    }

    // Erreurs basées sur le statut HTTP
    switch (error.response.status) {
      case 400:
        return 'La requête comporte des erreurs. Veuillez vérifier les données fournies.';
      case 401:
        return 'Session expirée. Veuillez vous reconnecter.';
      case 403:
        return 'Vous n\'avez pas les permissions nécessaires pour effectuer cette action.';
      case 404:
        return 'La ressource demandée n\'existe pas ou n\'est pas disponible actuellement.';
      case 422:
        return 'Les données fournies sont invalides. Veuillez vérifier le format.';
      case 429:
        return 'Trop de requêtes. Veuillez réessayer dans quelques instants.';
      case 500:
        return 'Une erreur est survenue sur le serveur. L\'équipe technique a été notifiée.';
      case 502:
      case 503:
      case 504:
        return 'Le service est temporairement indisponible. Veuillez réessayer plus tard.';
      default:
        return `Erreur inattendue (${error.response.status}). Veuillez réessayer ou contacter le support.`;
    }
  }
}

/**
 * Hook pour utiliser le gestionnaire d'erreurs API dans les composants React
 */
export function useApiErrorState() {
  const [state, setState] = React.useState<ApiServiceState | null>(null);

  React.useEffect(() => {
    const errorHandler = ApiErrorHandler.getInstance();
    const unsubscribe = errorHandler.subscribe(newState => {
      setState(newState);
    });

    // Cleanup on unmount
    return () => unsubscribe();
  }, []);

  return state;
}

export default ApiErrorHandler;
